import { useEffect, useRef } from "react";

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
const GSI_SRC = "https://accounts.google.com/gsi/client";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (cfg: { client_id: string; callback: (resp: { credential: string }) => void }) => void;
          renderButton: (el: HTMLElement, opts: Record<string, unknown>) => void;
        };
      };
    };
  }
}

function loadGsiScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve();
    const existing = document.querySelector(`script[src="${GSI_SRC}"]`) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener("load", () => resolve());
      return;
    }
    const s = document.createElement("script");
    s.src = GSI_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Failed to load Google script"));
    document.head.appendChild(s);
  });
}

/**
 * "Continue with Google" button (Google Identity Services). Renders Google's
 * official button; on sign-in it POSTs the ID token to /api/auth/google and
 * calls onSuccess. Renders nothing when VITE_GOOGLE_CLIENT_ID is unset, so the
 * feature stays hidden until configured.
 */
export function GoogleSignInButton({
  onSuccess,
  onError,
  // v6.50 — Google renders this button itself, so a dark page needs Google's
  // own dark variant; "outline" is a white slab that fights a black screen.
  theme = "outline",
  extraBody,
}: {
  onSuccess: () => void | Promise<void>;
  onError?: (msg: string) => void;
  theme?: "outline" | "filled_black" | "filled_blue";
  /** v7.28 — extra fields merged into the /api/auth/google body (promo coupon). */
  extraBody?: Record<string, unknown>;
}) {
  const divRef = useRef<HTMLDivElement>(null);
  // The Google callback is built once inside the effect below, so reading
  // extraBody directly there would capture a stale value. A ref stays live.
  const extraBodyRef = useRef<Record<string, unknown> | undefined>(extraBody);
  extraBodyRef.current = extraBody;

  useEffect(() => {
    if (!CLIENT_ID) return;
    let cancelled = false;
    loadGsiScript()
      .then(() => {
        if (cancelled || !window.google || !divRef.current) return;
        window.google.accounts.id.initialize({
          client_id: CLIENT_ID,
          callback: async (resp) => {
            try {
              const r = await fetch("/api/auth/google", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ credential: resp.credential, ...(extraBodyRef.current ?? {}) }),
              });
              if (!r.ok) {
                const data = await r.json().catch(() => ({}));
                onError?.(data.message || "Google sign-in failed");
                return;
              }
              await onSuccess();
            } catch {
              onError?.("Something went wrong. Please try again.");
            }
          },
        });
        window.google.accounts.id.renderButton(divRef.current, {
          theme,
          size: "large",
          width: 320,
          text: "continue_with",
        });
      })
      .catch(() => onError?.("Couldn't load Google sign-in."));
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!CLIENT_ID) return null;
  return <div ref={divRef} data-testid="google-signin-button" className="flex justify-center" />;
}
